/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { formatFullDate, HasLinkPipe } from '@common/tech-shared';
import { hasLink } from '@ngxp/rest';
import {
  ButtonComponent,
  CardColBadge,
  CardColComponent,
  CardColRightComponent,
  CardComponent,
  CardItemComponent,
  CollapsibleSectionComponent,
  CopyButtonComponent,
  TooltipDirective,
} from '@ods/system';
import { STATUS_BADGES, StatusBadge, UNKNOWN_STATUS_BADGE } from './certificate-card.model';
import {
  CA_ZERTIFIKAT_FILE_NAME,
  ZERTIFIKAT_FILE_NAME,
  ZertifikatDownload,
  ZertifikatLinkRel,
  ZertifikatResource,
  ZertifikatStatus,
} from './zertifikat.model';

@Component({
  selector: 'odc-certificate-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    CardColComponent,
    CardColRightComponent,
    CardComponent,
    CardItemComponent,
    CollapsibleSectionComponent,
    CopyButtonComponent,
    HasLinkPipe,
    TooltipDirective,
  ],
  host: {
    role: 'listitem',
  },
  templateUrl: './certificate-card.component.html',
})
export class CertificateCardComponent {
  public readonly zertifikat: InputSignal<ZertifikatResource> = input.required<ZertifikatResource>();
  public readonly detailsCollapsed = input(true, { transform: booleanAttribute });

  public readonly renewClicked: OutputEmitterRef<void> = output<void>();
  public readonly revokeClicked: OutputEmitterRef<void> = output<void>();
  public readonly downloadClicked: OutputEmitterRef<ZertifikatDownload> = output<ZertifikatDownload>();

  public readonly badges: Signal<CardColBadge[]> = computed(() => this._buildBadges(this.zertifikat()));

  public readonly validityText: Signal<string> = computed(() => this._buildValidityText(this.zertifikat()));

  public readonly isRenewActionVisible: Signal<boolean> = computed(() => hasLink(this.zertifikat(), ZertifikatLinkRel.RENEW));

  public readonly isRevokeActionVisible: Signal<boolean> = computed(() => hasLink(this.zertifikat(), ZertifikatLinkRel.REVOKE));

  public readonly signedCertificateDownload: ZertifikatDownload = {
    linkRel: ZertifikatLinkRel.CERTIFICATE,
    fileName: ZERTIFIKAT_FILE_NAME,
  };

  public readonly caCertificateDownload: ZertifikatDownload = {
    linkRel: ZertifikatLinkRel.CA_CERTIFICATE,
    fileName: CA_ZERTIFIKAT_FILE_NAME,
  };

  public readonly zertifikatLinkRel = ZertifikatLinkRel;

  _buildBadges(zertifikat: ZertifikatResource): CardColBadge[] {
    const status: StatusBadge = STATUS_BADGES[zertifikat.status] ?? UNKNOWN_STATUS_BADGE;
    const badges: CardColBadge[] = [{ ...status, dataTestId: 'certificate-card-status-badge' }];
    if (zertifikat.status === ZertifikatStatus.ABLAUFEND) {
      badges.push({
        name: 'Läuft bald ab',
        variant: 'outline',
        color: 'warning',
        dataTestId: 'certificate-card-expiring-soon-badge',
      });
    }
    return badges;
  }

  _buildValidityText(zertifikat: ZertifikatResource): string {
    return `Gültigkeit: bis ${formatFullDate(zertifikat.validUntil)}`;
  }
}
